"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type {
  CorrectionChoice,
  REPEATED_MEASURES_ANOVA_CALCULATOR_STRINGS,
} from "@/components/tools/repeated-measures-anova-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatDf,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type {
  CorrectedTest,
  RepeatedMeasuresAnova,
} from "@/lib/tool-kit/stats-tests-more/repeated-measures-anova";

/** Results for the repeated-measures ANOVA calculator. */

type Strings = typeof REPEATED_MEASURES_ANOVA_CALCULATOR_STRINGS;
type Used = "none" | "gg" | "hf";

/** Girden (1992): Greenhouse–Geisser below this ε, Huynh–Feldt above. */
const GIRDEN_CUTOFF = 0.75;

/** The correction a reader asked for, or the one Mauchly's test recommends. */
function resolveCorrection(
  r: RepeatedMeasuresAnova,
  choice: CorrectionChoice,
  alpha: number
): Used {
  if (choice !== "auto") {
    return choice;
  }
  const violated = r.sphericity.p !== null && r.sphericity.p < alpha;
  if (!violated) {
    return "none";
  }
  return r.sphericity.epsilonGG < GIRDEN_CUTOFF ? "gg" : "hf";
}

function testFor(r: RepeatedMeasuresAnova, used: Used): CorrectedTest {
  if (used === "gg") {
    return r.greenhouseGeisser;
  }
  if (used === "hf") {
    return r.huynhFeldt;
  }
  return {
    epsilon: 1,
    df1: r.dfConditions,
    df2: r.dfError,
    p: r.p,
  };
}

function SphericitySection({
  r,
  alpha,
  strings,
  fmt,
}: {
  r: RepeatedMeasuresAnova;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const s = r.sphericity;
  const rows: { key: string; label: string; test: CorrectedTest }[] = [
    { key: "none", label: strings.assumed, test: testFor(r, "none") },
    { key: "gg", label: strings.correctionGg, test: r.greenhouseGeisser },
    { key: "hf", label: strings.correctionHf, test: r.huynhFeldt },
  ];
  let verdict: string = strings.mauchlyUnavailable;
  if (s.p !== null) {
    verdict = t(s.p < alpha ? strings.mauchlyViolated : strings.mauchlyHolds, {
      alpha: fmt(alpha),
    });
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.sphericityHeading}</h3>
      {s.w === null && s.p === null && r.k === 2 ? (
        <p className={HINT}>{strings.sphericityNone}</p>
      ) : (
        <div className="space-y-2 text-foreground text-sm">
          {s.w !== null &&
          s.chiSquare !== null &&
          s.df !== null &&
          s.p !== null ? (
            <p>
              {t(strings.mauchly, {
                w: n(s.w),
                df: fmt(s.df),
                chi: n(s.chiSquare),
                p: formatP(s.p, fmt),
              })}
            </p>
          ) : null}
          <p>{verdict}</p>
          <p>
            {t(strings.epsilonLine, {
              gg: n(s.epsilonGG),
              hf: n(s.epsilonHF),
              lower: n(s.epsilonLower),
            })}
          </p>
          <p className={HINT}>{strings.guideline}</p>
        </div>
      )}
      <h3 className={WIDGET_H3}>{strings.correctedHeading}</h3>
      <ResultTable
        head={[
          strings.correction,
          strings.epsilon,
          strings.df1,
          strings.df2,
          strings.f,
          strings.p,
        ]}
        rows={rows.map((row) => ({
          key: row.key,
          cells: [
            row.label,
            n(row.test.epsilon),
            formatDf(row.test.df1, fmt),
            formatDf(row.test.df2, fmt),
            n(r.f),
            formatP(row.test.p, fmt),
          ],
        }))}
      />
    </>
  );
}

export function RepeatedMeasuresAnovaResults({
  r,
  names,
  alpha,
  choice,
  strings,
  fmt,
}: {
  r: RepeatedMeasuresAnova;
  names: readonly string[];
  alpha: number;
  choice: CorrectionChoice;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const used = resolveCorrection(r, choice, alpha);
  const shown = testFor(r, used);
  const reject = shown.p < alpha;
  let usedText: string = strings.usedNone;
  if (used !== "none") {
    usedText = t(used === "gg" ? strings.usedGg : strings.usedHf, {
      eps: n(shown.epsilon),
    });
  }
  const apaNote = {
    none: strings.apaNoteNone,
    gg: strings.apaNoteGg,
    hf: strings.apaNoteHf,
  }[used];
  const apa = richTemplate(strings.apaTemplate, {
    df1: formatDf(shown.df1, fmt),
    df2: formatDf(shown.df2, fmt),
    f: fixed(r.f, 2, fmt),
    p: apaP(shown.p, fmt),
    eta: fixed(r.partialEtaSquared, 2, fmt, false),
  });
  const meanTerms = r.means
    .map((m) => `(${n(m)} − ${n(r.grandMean)})²`)
    .join(" + ");
  const s = r.sphericity;
  const mauchlyApa =
    s.w !== null && s.chiSquare !== null && s.df !== null && s.p !== null
      ? richTemplate(strings.mauchlyApaTemplate, {
          w: fixed(s.w, 2, fmt, false),
          df: fmt(s.df),
          chi: fixed(s.chiSquare, 2, fmt),
          p: apaP(s.p, fmt),
        })
      : null;
  const rowCells = (
    label: string,
    ss: number,
    df: number,
    ms: number | null,
    f: number | null,
    p: number | null
  ) => [
    label,
    n(ss),
    fmt(df),
    ms === null ? "" : n(ms),
    f === null ? "" : n(f),
    p === null ? "" : formatP(p, fmt),
  ];
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.fStatistic} value={n(r.f)} />
        <StatCard
          label={strings.dfLabel}
          value={`${formatDf(shown.df1, fmt)}, ${formatDf(shown.df2, fmt)}`}
        />
        <StatCard
          emphasis
          label={strings.pValue}
          value={formatP(shown.p, fmt)}
        />
        <StatCard label={strings.partialEta} value={n(r.partialEtaSquared)} />
        <StatCard
          label={strings.generalizedEta}
          value={n(r.generalizedEtaSquared)}
        />
        <StatCard label={strings.subjects} value={fmt(r.n)} />
      </StatGrid>
      <p className={HINT}>{usedText}</p>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(shown.p, fmt),
        })}
      </Decision>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <ResultTable
        head={[
          strings.source,
          strings.ss,
          strings.df,
          strings.ms,
          strings.f,
          strings.p,
        ]}
        rows={[
          {
            key: "conditions",
            cells: rowCells(
              strings.conditions,
              r.ssConditions,
              r.dfConditions,
              r.msConditions,
              r.f,
              r.p
            ),
          },
          {
            key: "subjects",
            cells: rowCells(
              strings.subjectsRow,
              r.ssSubjects,
              r.dfSubjects,
              r.msSubjects,
              null,
              null
            ),
          },
          {
            key: "error",
            cells: rowCells(
              strings.error,
              r.ssError,
              r.dfError,
              r.msError,
              null,
              null
            ),
          },
          {
            key: "total",
            cells: rowCells(
              strings.total,
              r.ssTotal,
              r.dfConditions + r.dfSubjects + r.dfError,
              null,
              null,
              null
            ),
          },
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.descriptivesHeading}</h3>
      <ResultTable
        head={[strings.condition, strings.mean, strings.sd]}
        rows={names.map((name, j) => ({
          key: `${j}-${name}`,
          cells: [name, n(r.means[j]), n(r.sds[j])],
        }))}
      />
      <SphericitySection alpha={alpha} fmt={fmt} r={r} strings={strings} />
      <h3 className={WIDGET_H3}>{strings.posthocHeading}</h3>
      <p className={HINT}>
        {t(strings.posthocHint, { n: fmt(r.n), m: fmt(r.pairs.length) })}
      </p>
      <ResultTable
        head={[
          strings.comparison,
          strings.difference,
          strings.se,
          strings.t,
          strings.p,
          strings.pBonferroni,
          strings.pHolm,
          strings.dz,
        ]}
        rows={r.pairs.map((pair) => ({
          key: `${pair.first}-${pair.second}`,
          cells: [
            t(strings.versus, { a: names[pair.first], b: names[pair.second] }),
            n(pair.meanDifference),
            n(pair.se),
            n(pair.t),
            formatP(pair.p, fmt),
            formatP(pair.pBonferroni, fmt),
            formatP(pair.pHolm, fmt),
            n(pair.dz),
          ],
        }))}
      />
      <Working heading={strings.howCalculated}>
        <p>
          SS_conditions = n Σ (x̄ⱼ − x̄)² = {fmt(r.n)} × ({meanTerms}) ={" "}
          {n(r.ssConditions)}
        </p>
        <p>
          SS_subjects = k Σ (x̄ᵢ − x̄)² = {n(r.ssSubjects)}; SS_total = Σ (x − x̄)²
          = {n(r.ssTotal)}
        </p>
        <p>
          SS_error = SS_total − SS_conditions − SS_subjects = {n(r.ssTotal)} −{" "}
          {n(r.ssConditions)} − {n(r.ssSubjects)} = {n(r.ssError)}
        </p>
        <p>
          MS_conditions = {n(r.ssConditions)} / {fmt(r.dfConditions)} ={" "}
          {n(r.msConditions)}; MS_error = {n(r.ssError)} / {fmt(r.dfError)} ={" "}
          {n(r.msError)}
        </p>
        <p>
          F = MS_conditions / MS_error = {n(r.msConditions)} / {n(r.msError)} ={" "}
          {n(r.f)}; ηₚ² = SS_conditions / (SS_conditions + SS_error) ={" "}
          {n(r.partialEtaSquared)}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={apaNote}
        parts={apa}
      />
      {mauchlyApa ? (
        <ApaReport
          copiedLabel={strings.copied}
          copyLabel={strings.copyApa}
          heading={strings.mauchlyApaHeading}
          parts={mauchlyApa}
        />
      ) : null}
    </section>
  );
}
